import React, { useState, useCallback, useEffect } from 'react';
import cn from 'classnames';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import { DndSortable, DndSortableElement } from '../DndSortable';
import { DraggableType } from '../../types/dnd';
import DndProviderWrapper from '../DndProviderWrapper';
import { DndSortableElementRenderOptions } from '../DndSortable/types';

import s from './DndSortableList.scss';

const DRAG_THROTTLE = 250;

interface Props {
  dndType: DraggableType;
  items: any[];
  children: (item: any, options: DndSortableElementRenderOptions, index: number) => React.ReactNode;
  onDragOrder: (item: any, position: number, initialPosition: number) => void;
}

const DndSortableList: React.FC<Props> = ({ dndType, items, children, onDragOrder }) => {
  const [dragPosition, setDragPosition] = useState<number | null>(null);
  const [sortedItems, setSortedItems] = useState(items);

  const onDragOver = useThrottle(
    (id: number, position: number, initialPosition: number) => {
      const newItems = [...items];
      const item = newItems.splice(initialPosition - 1, 1)[0];
      newItems.splice(position - 1, 0, item);
      setDragPosition(position);
      setSortedItems(newItems);
    },
    [items],
    DRAG_THROTTLE,
    { leading: true, trailing: false }
  );

  const onDragEnd = useCallback(
    (id: number, initialPosition: number) => {
      setDragPosition(null);
      if (dragPosition !== null && dragPosition !== initialPosition) {
        const item = items.find(i => i.id === id);
        onDragOrder(item, dragPosition - 1, initialPosition - 1);
      }
    },
    [dragPosition, onDragOrder, items]
  );

  useEffect(() => setSortedItems(items), [items]);

  return (
    <DndProviderWrapper>
      <DndSortable onDragOver={onDragOver} onDragEnd={onDragEnd}>
        {sortableProps => {
          const { draggedElementId, ...dndProps } = sortableProps;
          return sortedItems.map((item, idx) => (
            <DndSortableElement
              dndType={dndType}
              dndAcceptType={dndType}
              id={item.id}
              key={item.id}
              position={idx + 1}
              {...dndProps}
            >
              {sortableOptions => {
                const isDragging = draggedElementId === item.id;
                const content = children(item, sortableOptions, idx);
                return (
                  content && (
                    <div
                      key={item.id}
                      ref={sortableOptions.ref}
                      className={cn(isDragging && s['dragging'])}
                    >
                      {content}
                    </div>
                  )
                );
              }}
            </DndSortableElement>
          ));
        }}
      </DndSortable>
    </DndProviderWrapper>
  );
};
DndSortableList.displayName = 'DndSortableList';

export default DndSortableList;
